import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import {
  resolveRedirectTarget,
  type RedirectStateFrom,
} from '@/shared/libs/redirect';
import {
  rememberOnboardingReturn,
  takeOnboardingReturn,
} from '@/shared/libs/onboarding-return';
import { useAuth, useOnboardingState } from '../api/use-auth.query';
import { useLogout, useRefreshSession } from '../api/use-auth.mutation';
import { AccountSetupStep } from '../components/onboarding/AccountSetupStep';
import { EmailVerifyStep } from '../components/onboarding/EmailVerifyStep';
import { LoginIdResetStep } from '../components/onboarding/LoginIdResetStep';
import { PhoneVerifyStep } from '../components/onboarding/PhoneVerifyStep';
import { OnboardingStepper } from '../components/onboarding/OnboardingStepper';
import type { OnboardingStepItem } from '../types/onboarding-step.type';

/**
 * 첫 로그인 온보딩 View — Owner 가 만든 구성원 계정·구버전(HowCost+AWS) 이관 계정.
 *
 * 두 계정 모두 이 플랫폼에서 본인이 만든 게 아니다(관리자가 정해 줬거나 구버전에서 옮겨 왔다).
 * 그래서 서비스를 쓰기 전에 본인 손으로 확정하게 한다(이미 끝난 단계는 건너뛴다):
 *   0. 계정 재설정 — 이관 계정 중 아이디가 바뀌었거나 예약어인 계정만. 새 아이디를 확정한다
 *   1. 계정 설정   — 새 비밀번호(관리자가 알던 값을 닫는다) + 필수 약관 동의(본인 기록이어야 증빙이 된다)
 *   2. 이메일 인증 — 메일로 받은 인증번호를 이 화면에서 입력(이관 때 이메일이 없던 계정은 새 주소부터)
 *   3. 휴대폰 인증 — 번호 소유 증명. 확인이 곧 완료(구버전 공유 계정은 유예 마감 전까지 "나중에 하기")
 *
 * 모든 단계가 한 화면 안에서 이어진다 — 재로그인·탭 이동·새로고침이 없어야 한다.
 * 비밀번호 변경 응답이 이 세션의 새 쿠키를 내려주고, 인증은 링크가 아니라 인증번호로 받는다.
 *
 * **차단은 이 화면이 하지 않는다.** Gateway 가 `status = PENDING_ONBOARDING` 인 토큰을
 * 온보딩 라우트 밖에서 전부 막는다 — 화면에서만 막으면 API 직접 호출로 우회된다.
 */
export function OnboardingView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { data: state, isLoading } = useOnboardingState();
  const { mutate: logout, isPending: loggingOut } = useLogout();

  // 모든 축이 끝나면 서버가 계정을 ACTIVE 로 올린다 — 그때 원래 가려던 곳으로.
  // 지금 토큰은 아직 PENDING_ONBOARDING 클레임이라 Gateway 가 막는다. refresh 로
  // ACTIVE 토큰을 받은 뒤 이동한다(데스크톱 로그인 동의처럼 돌아갈 곳이 있는 경우).
  // AuthGuard 가 넘긴 복귀 경로는 탭 저장소로 옮겨 둔다 — 라우터 state 는 이동 중에 사라진다.
  const stateFrom = (location.state as { from?: RedirectStateFrom } | null)
    ?.from;
  useEffect(() => {
    if (stateFrom) {
      rememberOnboardingReturn(
        resolveRedirectTarget({ stateFrom, fallback: PATHS.MYPAGE }),
      );
    }
  }, [stateFrom]);

  const finished = !!state && !state.required;
  const { mutateAsync: refreshSession } = useRefreshSession();
  const leavingRef = useRef(false);
  useEffect(() => {
    if (!finished || leavingRef.current) return;
    leavingRef.current = true;
    const target = resolveRedirectTarget({
      queryFrom: takeOnboardingReturn(),
      fallback: PATHS.MYPAGE,
    });
    // refresh 가 실패해도 이동은 한다 — 다음 401 에서 클라이언트가 다시 갱신한다.
    void refreshSession()
      .catch(() => undefined)
      .then(() => navigate(target, { replace: true }));
  }, [finished, navigate, refreshSession]);

  if (isLoading || !state) {
    return (
      <div className="mx-auto w-full max-w-md py-10" aria-busy="true">
        <div className="h-96 animate-pulse rounded-2xl bg-neutral-100 dark:bg-white/5" />
      </div>
    );
  }

  const consentDone = state.consentsRequired.length === 0;
  const steps: OnboardingStepItem[] = [
    ...(state.loginIdChangeRequired || state.loginIdConfirmed
      ? [
          {
            key: 'login-id',
            label: '계정 재설정',
            done: state.loginIdConfirmed,
          },
        ]
      : []),
    {
      key: 'account',
      label: state.passwordChanged ? '약관 동의' : '계정 설정',
      done: state.passwordChanged && consentDone,
    },
    { key: 'email', label: '이메일 인증', done: state.emailVerified },
    { key: 'phone', label: '휴대폰 인증', done: state.phoneVerified },
  ];
  const current = finished ? undefined : steps.find((s) => !s.done)?.key;

  return (
    <div className="mx-auto w-full max-w-md py-10">
      <header className="mb-6">
        <p className="text-xs font-bold text-brand-700 dark:text-brand-400">
          최초 설정
        </p>
        <h1 className="mt-1 text-2xl font-bold -tracking-[0.02em] text-neutral-950 dark:text-neutral-100">
          {user?.name ? `${user.name}님, 환영합니다` : '환영합니다'}
        </h1>
        <p className="mt-1 break-keep text-sm text-neutral-600 dark:text-neutral-400">
          서비스를 이용하기 전에 계정 확인을 마쳐 주세요. 이 화면에서 바로
          끝납니다.
        </p>
        {/* 구버전 아이디가 신 규칙으로 바뀐 계정도 있어 지금 로그인 아이디를 알려 둔다. */}
        {user?.loginId && (
          <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-neutral-100 px-3 py-1 text-xs text-neutral-600 dark:bg-white/5 dark:text-neutral-400">
            로그인 아이디
            <b className="font-mono text-neutral-950 dark:text-neutral-100">
              {user.loginId}
            </b>
          </p>
        )}
      </header>

      <OnboardingStepper steps={steps} current={current} />

      <section className="mt-6 rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-dark-card">
        {current === 'login-id' && (
          <LoginIdResetStep
            key={user?.loginId}
            currentLoginId={user?.loginId ?? ''}
          />
        )}
        {current === 'account' && (
          <AccountSetupStep
            needsPassword={!state.passwordChanged}
            needsConsent={!consentDone}
          />
        )}
        {current === 'email' && (
          <EmailVerifyStep
            email={user?.email ?? ''}
            replacementRequired={state.emailReplacementRequired}
          />
        )}
        {current === 'phone' && (
          <PhoneVerifyStep
            initialPhone={user?.phone ?? ''}
            deferUntil={
              state.phoneDeferrable
                ? (state.phoneVerifyDueAt ?? undefined)
                : undefined
            }
          />
        )}
        {!current && <FinishingPanel />}
      </section>

      {/*
        빠져나갈 문 — 온보딩을 마치기 전에는 Gateway 가 다른 모든 경로를 막으므로,
        이게 없으면 계정을 잘못 받은 사람이 쿠키를 지우는 것 말고는 방법이 없다.
      */}
      {!finished && (
        <p className="mt-6 text-center text-xs">
          <button
            type="button"
            onClick={() => logout()}
            disabled={loggingOut}
            className="text-neutral-500 underline-offset-2 hover:text-neutral-950 hover:underline disabled:opacity-50 dark:text-neutral-400 dark:hover:text-neutral-100"
          >
            다른 계정으로 로그인
          </button>
        </p>
      )}
    </div>
  );
}

function FinishingPanel() {
  return (
    <div role="status" className="flex flex-col items-center py-6 text-center">
      <span className="grid h-12 w-12 place-items-center rounded-full bg-success/15 text-success">
        <svg width="22" height="22" viewBox="0 0 12 12" fill="none" aria-hidden>
          <path
            d="M2.5 6.5L5 9L9.5 3.5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <p className="mt-4 text-lg font-bold text-neutral-950 dark:text-neutral-100">
        설정이 완료되었습니다
      </p>
      <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
        잠시 후 이동합니다…
      </p>
    </div>
  );
}
